import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { Box, Button, IconButton, Typography } from "@mui/material";
import React, { useState } from "react";
import swal from "sweetalert";
import ConfirmationModal from "../../components/ConfirmationModal";
import CustomTable from "../../components/CustomTable";
import MainLayout from "../../components/MainLayout";
import axiosInstance from "../../utils/axiosInstance";
import { checkPermission } from "../../utils/checkPermission";
import { formatNumber } from "../../utils/formatters";
import ServiceCenterDetailsModal from "./detail";
import ServiceCenterModal from "./modal";

export default function ServiceCenters() {
  const [refresh, setRefresh] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);

  const columns = [
    {
      filed: "",
      headerName: "",
      width: 70,
      sortable: false,
      filterable: false,
      type: "actions",
      renderCell: (value) => (
        <Box>
          {checkPermission("centers-view") && (
            <IconButton
              color="info"
              onClick={() => {
                setCurrentRow(value.row);
                setOpenDetails(true);
              }}
            >
              <RemoveRedEyeIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
    { field: "id", headerName: "ID", width: 70, type: "number" },
    { field: "title", headerName: "Title", width: "350" },
    {
      field: "service_type",
      headerName: "Service Type",
      width: "100",
      valueGetter: (_value, row) => `${row.service_type.name}`,
      sortable: false,
    },
    { field: "email", headerName: "Email", width: "200" },
    { field: "phone", headerName: "Phone", width: "200" },
    { field: "contact_person", headerName: "Contact Person", width: "200" },
    {
      field: "location",
      headerName: "Location",
      width: "200",
      valueGetter: (_value, row) => `${row.latitude}, ${row.longitude}`,
    },
    {
      field: "rating",
      headerName: "Rating",
      width: "140",
      valueGetter: (value, row) =>
        `${formatNumber(value, 1)} (${row.reviews.length} reviews)`,
    },
    {
      field: "",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      type: "actions",
      renderCell: (value) => (
        <Box>
          {checkPermission("centers-update") && (
            <IconButton
              color="info"
              onClick={() => {
                setCurrentRow(value.row);
                setOpenEdit(true);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
          {checkPermission("centers-update") && (
            <IconButton
              color="error"
              onClick={() => {
                setCurrentRow(value.row);
                setOpenConfirm(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const handleModalClose = () => {
    setOpenAdd(false);
    setOpenEdit(false);
    setOpenConfirm(false);
    setOpenDetails(false);
    setCurrentRow(null);
  };

  const handleServiceCenterAdd = async (values) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/centers`,
        values,
      );

      if (response.status === 201) {
        setRefresh(!refresh);
        handleModalClose();
        swal("Successfully created service center", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to create service center. Please try again.", {
        icon: "error",
      });
    }
  };

  const handleServiceCenterEdit = async (values, id) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/centers/${id}/edit`,
        values,
      );

      if (response.status === 200) {
        setRefresh(!refresh);
        handleModalClose();
        swal("Successfully updated service center", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to update service center. Please try again.", {
        icon: "error",
      });
    }
  };

  const handleServiceCenterDelete = async () => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/centers/${currentRow.id}`,
      );

      if (response.status === 204) {
        setRefresh(!refresh);
        handleModalClose();
        swal("Successfully deleted service center", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to delete service center. Please try again.", {
        icon: "error",
      });
    }
  };

  return (
    <>
      <MainLayout>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Service Centers</Typography>

          {checkPermission("centers-create") && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAdd(true)}
            >
              Add new
            </Button>
          )}
        </Box>

        <CustomTable
          columns={columns}
          fetchDataUrl="centers-paginated"
          refresh={refresh}
        />
      </MainLayout>

      {/* Detail view */}
      <ServiceCenterDetailsModal
        open={openDetails}
        onClose={handleModalClose}
        serviceCenter={currentRow}
      />

      <ServiceCenterModal
        open={openAdd}
        onClose={handleModalClose}
        onSubmit={handleServiceCenterAdd}
      />

      {currentRow && openEdit && (
        <ServiceCenterModal
          open={openEdit}
          onClose={handleModalClose}
          onSubmit={handleServiceCenterEdit}
          serviceCenter={currentRow}
        />
      )}

      {currentRow && (
        <ConfirmationModal
          open={openConfirm}
          onClose={handleModalClose}
          onConfirm={handleServiceCenterDelete}
          title="Are you sure you want to delete this service center?"
          message={
            <span>
              <strong>{currentRow.title}</strong> service center will be
              deleted. Please note that this action is not reversible.
            </span>
          }
        />
      )}
    </>
  );
}
